// Spam audit page — Polish #2.
//
// Route: /spam
// Shape: mini top nav + heading + intro + list of threads the local matcher
// flagged as spam over the last 30 days. This is the ONLY place Kimi ever
// sees caught spam; the main Standup hides them entirely now.
//
// Actions per row:
//   - Open thread (jumps to Command Center so she can reply if legit)
//   - Mark resolved (bulk-status call; skips the ones already resolved)
//
// Top-level "Resolve all" handles the common case where everything on the
// list really is spam and she wants to clear it in one click. The matcher
// is deliberately conservative so false positives are rare, but the thread
// lives on if she ever wants to reopen it.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '@/components/brand/Logo';
import { useAuth } from '@/auth/AuthProvider';
import { Tag } from '@/components/brand/Tag';
import type { TagVariant } from '@/components/brand/Tag';
import type { Channel } from '@md/shared';
import { useSpamInbox, type SpamAuditRow } from '@/lib/hooks/useSpamInbox';
import { useBulkStatus } from '@/lib/hooks/useBulkStatus';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

const CHANNEL_LABEL: Record<Channel, string> = {
  gmail: 'Gmail',
  etsy_md: 'Etsy',
  etsy_hk: 'Etsy',
  amazon: 'Amazon',
  shopify_md: 'Shopify',
  shopify_brokerage: 'Shopify',
  shipstation: 'ShipStation',
};

const CHANNEL_TAG: Record<Channel, TagVariant> = {
  gmail: 'ch-gmail',
  etsy_md: 'ch-etsy',
  etsy_hk: 'ch-etsy',
  amazon: 'ch-amzn',
  shopify_md: 'ch-shop',
  shopify_brokerage: 'ch-shop',
  shipstation: 'ch-shop',
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const age = now - d.getTime();
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  if (age < HOUR) return `${Math.max(1, Math.floor(age / 60_000))}m ago`;
  if (age < DAY) return `${Math.floor(age / HOUR)}h ago`;
  if (age < 7 * DAY) return `${Math.floor(age / DAY)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function excerptShort(body: string): string {
  // Strip leading greetings, collapse whitespace, cap at ~200 chars so the
  // list stays scannable without hiding the part that matters.
  const flat = body.replace(/\s+/g, ' ').trim();
  if (flat.length <= 200) return flat;
  return flat.slice(0, 200).trimEnd() + '…';
}

function SpamRow({
  row,
  onOpen,
  onResolve,
  busy,
}: {
  row: SpamAuditRow;
  onOpen: (id: string) => void;
  onResolve: (id: string) => void;
  busy: boolean;
}) {
  const already = row.status === 'resolved';
  return (
    <div
      style={{
        padding: '14px 16px',
        background: 'white',
        border: '1px solid hsl(var(--ink) / 0.1)',
        borderRadius: 10,
        marginBottom: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 6,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <Tag variant={row.brand === 'md' ? 'brand-md' : 'brand-hk'}>
            {row.brand === 'md' ? 'MD' : 'HK'}
          </Tag>
          <Tag variant={CHANNEL_TAG[row.channel]}>{CHANNEL_LABEL[row.channel]}</Tag>
          <span
            style={{
              fontSize: 12,
              color: 'hsl(var(--ink) / 0.55)',
              textTransform: 'capitalize',
            }}
          >
            {row.status}
          </span>
        </div>
        <span style={{ fontSize: 12, color: 'hsl(var(--ink) / 0.55)' }}>
          {formatWhen(row.last_message_at)}
        </span>
      </div>
      <div
        style={{ fontWeight: 600, color: 'hsl(var(--ink))', marginBottom: 2 }}
      >
        {row.subject && row.subject.trim().length > 0 ? row.subject : '(no subject)'}
      </div>
      <div style={{ fontSize: 13, color: 'hsl(var(--ink) / 0.65)', marginBottom: 8 }}>
        {row.sender_name ?? row.customer_email ?? 'unknown sender'}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'hsl(var(--ink) / 0.8)',
          lineHeight: 1.5,
          marginBottom: 10,
        }}
      >
        {excerptShort(row.excerpt)}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => onOpen(row.id)}
          style={{
            padding: '6px 12px',
            border: '1px solid hsl(var(--ink) / 0.15)',
            borderRadius: 8,
            background: 'white',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Open thread
        </button>
        {already ? (
          <span
            style={{
              alignSelf: 'center',
              fontSize: 12,
              color: 'hsl(var(--ink) / 0.45)',
            }}
          >
            Already resolved
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onResolve(row.id)}
            disabled={busy}
            style={{
              padding: '6px 12px',
              border: '1px solid hsl(var(--ink) / 0.15)',
              borderRadius: 8,
              background: 'hsl(var(--monstera) / 0.08)',
              color: 'hsl(var(--monstera))',
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            Mark resolved
          </button>
        )}
      </div>
    </div>
  );
}

export function SpamPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const initials = (user?.email ?? 'K').charAt(0).toUpperCase();

  const { data: rows = [], isLoading, error, refetch } = useSpamInbox();
  const bulkStatus = useBulkStatus();
  const [confirmAll, setConfirmAll] = useState(false);

  const openIds = useMemo(
    () => rows.filter((r) => r.status !== 'resolved').map((r) => r.id),
    [rows],
  );

  const onResolveOne = (id: string) => {
    bulkStatus.mutate(
      { ids: [id], status: 'resolved' },
      { onSuccess: () => void refetch() },
    );
  };

  const onResolveAll = () => {
    if (openIds.length === 0) return;
    bulkStatus.mutate(
      { ids: openIds, status: 'resolved' },
      {
        onSuccess: () => {
          setConfirmAll(false);
          void refetch();
        },
      },
    );
  };

  return (
    <>
      {/* Mini top nav — logo + Back + avatar (mirrors Search) */}
      <header className="topnav">
        <div className="wrap">
          <div className="topnav-inner">
            <a href="/" className="logo-mark" aria-label="Market Dwellings">
              <Logo />
            </a>
            <nav
              className="viewnav"
              aria-label="Spam audit nav"
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              <button type="button" onClick={() => navigate('/')}>
                <span aria-hidden style={{ marginRight: 6 }}>←</span>
                Back to Standup
              </button>
            </nav>
            <div className="top-right">
              <button
                type="button"
                className="avatar"
                aria-label="Account menu — sign out"
                title={user?.email ?? 'Signed in'}
                onClick={() => void signOut()}
              >
                {initials}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="wrap" style={{ paddingTop: 24, paddingBottom: 48 }}>
        <h1 className="display h2" style={{ marginTop: 0, marginBottom: 8 }}>
          Spam audit
        </h1>
        <p
          style={{
            margin: '0 0 20px',
            color: 'hsl(var(--ink) / 0.6)',
            maxWidth: 640,
          }}
        >
          Threads the local filter caught over the last 30 days. The matcher is
          tuned conservatively, so most of these are cold outreach. If something
          legitimate got in, open the thread and reply as usual.
        </p>

        {error ? (
          <div style={{ padding: '24px 0', color: 'hsl(var(--clay))' }}>
            Could not load: {(error as Error).message}
          </div>
        ) : isLoading ? (
          <div style={{ padding: '24px 0', color: 'hsl(var(--ink) / 0.55)' }}>
            Scanning…
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '24px 0', color: 'hsl(var(--ink) / 0.55)' }}>
            Nothing caught. Your inbox is quiet.
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 13, color: 'hsl(var(--ink) / 0.55)' }}>
                {rows.length} caught · {openIds.length} still open
              </div>
              {openIds.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setConfirmAll(true)}
                  disabled={bulkStatus.isPending}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid hsl(var(--ink) / 0.15)',
                    borderRadius: 8,
                    background: 'hsl(var(--monstera) / 0.1)',
                    color: 'hsl(var(--monstera))',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Resolve all {openIds.length}
                </button>
              ) : null}
            </div>
            <div>
              {rows.map((r) => (
                <SpamRow
                  key={r.id}
                  row={r}
                  onOpen={(id) => navigate(`/thread/${id}`)}
                  onResolve={onResolveOne}
                  busy={bulkStatus.isPending}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmAll}
        title={`Resolve ${openIds.length} spam thread${openIds.length === 1 ? '' : 's'}?`}
        body="Each thread moves to Resolved and leaves the audit view. They stay searchable, but you won't see them on the audit again unless the matcher re-catches something new."
        confirmLabel="Resolve all"
        cancelLabel="Cancel"
        danger={false}
        busy={bulkStatus.isPending}
        onCancel={() => {
          if (!bulkStatus.isPending) setConfirmAll(false);
        }}
        onConfirm={onResolveAll}
      />
    </>
  );
}
